import styled, { css } from 'styled-components';

import { size } from '../../layout/helpers';
import { ButtonReset } from '../../layout/reset';
import { Link, RouterLink } from '../Link';

interface WrapperProps {
  $tone?: 'success' | 'warning' | 'error' | 'info';
}

export const Wrapper = styled.div.attrs(() => ({
  role: 'alert',
}))<WrapperProps>`
  --alert-bg: var(--c-accent-bg);
  --alert-fg: var(--c-accent-fg);
  --alert-icon-bg: var(--c-base-bg);
  --alert-icon-fg: var(--c-base-fg);

  display: flex;
  align-items: center;
  gap: ${size(2)};
  width: 100%;
  padding: ${size(1.5)} ${size(2)};
  background-color: var(--alert-bg);
  color: var(--alert-fg);
  border-radius: ${size(1)};
  white-space: pre-line;
  font-size: ${(props) => props.theme.font.size.small};

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          --alert-bg: var(--c-alert-success-bg);
          --alert-icon-bg: var(--c-alert-success-icon-bg);
          --alert-icon-fg: var(--c-alert-success-icon-fg);
        `
      : $tone === 'warning'
        ? css`
            --alert-bg: var(--c-alert-warning-bg);
            --alert-icon-bg: var(--c-alert-warning-icon-bg);
            --alert-icon-fg: var(--c-alert-warning-icon-fg);
          `
        : $tone === 'error'
          ? css`
              --alert-bg: var(--c-alert-error-bg);
              --alert-icon-bg: var(--c-alert-error-icon-bg);
              --alert-icon-fg: var(--c-alert-error-icon-fg);
            `
          : $tone === 'info'
            ? css`
                --alert-bg: var(--c-alert-info-bg);
                --alert-icon-bg: var(--c-alert-info-icon-bg);
                --alert-icon-fg: var(--c-alert-info-icon-fg);
              `
            : undefined}
`;

export const Icon = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${size(3)};
  height: ${size(3)};
  border-radius: ${size(1)};
  background-color: var(--alert-icon-bg);
  color: var(--alert-icon-fg);
  font-size: ${(props) => props.theme.font.size.regular};
`;

export const Content = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${size(0.25)};
  line-height: ${(props) => props.theme.font.lineHeight.alt};

  ul {
    padding-left: ${size(4)};
    margin: ${size(1.5)} 0;
    font-size: ${(props) => props.theme.font.size.small};
    font-weight: var(--font-weight-bold);
  }
`;

export const Body = styled.div`
  font-size: ${(props) => props.theme.font.size.small};
`;

export const Heading = styled.div`
  margin-bottom: ${size(0.25)};
  font-weight: var(--font-weight-bold);
  font-size: ${(props) => props.theme.font.size.small};
`;

export const Action = styled(ButtonReset)`
  display: flex;
  align-items: center;
  gap: ${size(0.5)};
  font-weight: var(--font-weight-bold);
  font-size: ${(props) => props.theme.font.size.small};
  user-select: none;
  text-align: left;
  line-height: inherit;

  &:hover {
    text-decoration: underline;
  }
`;

export const ActionLink = styled(Link)`
  font-size: ${(props) => props.theme.font.size.small};
  color: currentColor;
`;

export const ActionRouterLink = styled(RouterLink)`
  font-size: ${(props) => props.theme.font.size.small};
  color: currentColor;
`;

export const CloseBtn = styled(ButtonReset)`
  position: relative;
  display: block;
  font-weight: var(--font-weight-bold);
  font-size: ${(props) => props.theme.font.size.xxlarge};
  border-radius: ${size(1)};
  user-select: none;
  isolation: isolate;

  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  // Hover indicator
  &::before {
    width: ${size(3)};
    height: ${size(3)};
    background-color: rgba(var(--c-overlay-rgb), 0.1);
    border-radius: inherit;
    z-index: -1;
    opacity: 0;
    transition: opacity 200ms ease;
  }

  // Hitbox
  &::after {
    width: ${size(5)};
    height: ${size(5)};
  }

  &:enabled {
    &:active {
      transform: scale(0.95);
    }

    &:hover {
      &::before {
        opacity: 1;
      }
    }
  }

  &[disabled] {
    opacity: 0.37;
    cursor: not-allowed;
  }
`;
